import React, {useCallback, useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  SxProps,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import {DateTime} from 'luxon';
import {useTranslation} from 'react-i18next';
import VideocamIcon from '@mui/icons-material/Videocam';
import {PositionGroup} from '../../enums';
import {
  Drill,
  DrillDetailRecord,
  TeamSessionSessionEvent,
  TeamSession,
} from '../../interfaces';
import {formatNum, formatTime} from '../../services/formatService';
import {getSpeedMultiplier} from '../../services/measurementService';
import {sanitizeFilename} from '../../helpers';

interface Props {
  sx?: SxProps;
  data: {
    drill?: Drill;
    drillDetail: DrillDetailRecord;
    teamSession?: TeamSession;
    teamSessionSessionEvent: TeamSessionSessionEvent;
  }[];
  page: 'drillDetail' | 'teamSession';
  preferredUnits?: 'imperial' | 'metric';
  title?: React.ReactNode;
  onVideoSelect?: (videoUrl: string) => void;
  drillName?: string;
  teamName?: string;
}

type DetailRow = DrillDetailRecord & {
  sessionDate: Date;
  sessionTime: number;
  duration: number;
  sessionType: string;
  name: string;
};

const drillDetailColumns = (
  t: (key: string) => string,
  page: 'drillDetail' | 'teamSession',
  preferredUnits?: 'imperial' | 'metric',
): GridColDef[] => [
  page === 'drillDetail'
    ? {
        field: 'sessionDate',
        headerName: t('library.sessionDate'),
        width: 120,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
            {params.row.videoUrl && (
              <VideocamIcon sx={{fontSize: 16, color: 'primary.main'}} />
            )}
            <span>
              {params.value
                ? DateTime.fromJSDate(params.value).toFormat('M/d/yyyy')
                : '-'}
            </span>
          </Box>
        ),
      }
    : {
        field: 'sessionTime',
        headerName: t('library.sessionTime'),
        width: 150,
        renderCell: (params: GridRenderCellParams) => (
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
            {params.row.videoUrl && (
              <VideocamIcon sx={{fontSize: 16, color: 'primary.main'}} />
            )}
            <span>{params.value ? formatTime(params.value) : '-'}</span>
          </Box>
        ),
      },
  page === 'drillDetail'
    ? {
        field: 'sessionType',
        headerName: t('general.type'),
        width: 70,
      }
    : {
        field: 'name',
        headerName: t('general.name'),
        width: 200,
        valueFormatter: params => params.value ?? '-',
      },
  {
    field: 'duration',
    headerName: t('general.duration'),
    width: 90,
    type: 'number',
    valueFormatter: params => formatTime(params.value),
  },
  {
    field: 'playerLoadAvg',
    headerName: t('library.avgPlayerLoad'),
    width: 140,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: 0.01,
      }),
  },
  {
    field: 'playerLoadPerMinAvg',
    headerName: t('library.avgPlayerLoadPerMin'),
    width: 170,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: 0.01,
        decimals: 2,
      }),
  },
  {
    field: 'drillRunCountAvg',
    headerName: t('library.avgRuns'),
    width: 140,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        decimals: 1,
      }),
  },
  {
    field: 'drillRunDurationAvg',
    headerName: t('library.avgRunDuration'),
    width: 150,
    type: 'number',
    valueFormatter: params => formatTime(params.value),
  },
  {
    field: 'speedAvgAvg',
    headerName: t('library.avgSpeed'),
    width: 110,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: getSpeedMultiplier(preferredUnits === 'metric'),
        unit: preferredUnits === 'metric' ? 'kph' : 'mph',
      }),
  },
  {
    field: 'speedMaxAvg',
    headerName: t('library.avgMaxSpeed'),
    width: 110,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: getSpeedMultiplier(preferredUnits === 'metric'),
        unit: preferredUnits === 'metric' ? 'kph' : 'mph',
      }),
  },
  {
    field: 'accelAvgAvg',
    headerName: t('library.avgAccel'),
    width: 110,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: 1 / 9.8,
        decimals: 2,
        unit: 'g',
      }),
  },
  {
    field: 'accelMaxAvg',
    headerName: t('library.avgMaxAccel'),
    width: 110,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: 1 / 9.8,
        decimals: 2,
        unit: 'g',
      }),
  },
  {
    field: 'strideRateAvgAvg',
    headerName: t('library.avgStrideRate'),
    width: 140,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        decimals: 0,
        unit: 'spm',
      }),
  },
  {
    field: 'balanceNormalizedAvg',
    headerName: t('library.avgBalance'),
    width: 120,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        multiplier: 100,
        decimals: 0,
        unit: '%',
      }),
  },
  {
    field: 'hrAvgAvg',
    headerName: t('library.avgHR'),
    width: 90,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        decimals: 0,
        unit: 'bpm',
      }),
  },
  {
    field: 'hrMaxAvg',
    headerName: t('library.avgMaxHR'),
    width: 90,
    type: 'number',
    valueFormatter: params =>
      formatNum({
        value: params.value,
        decimals: 0,
        unit: 'bpm',
      }),
  },
];

const DrillDetailsDataGrid: React.FC<Props> = ({
  sx,
  data,
  page,
  preferredUnits,
  title,
  onVideoSelect,
  drillName,
  teamName,
}) => {
  const {t} = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState<string>(PositionGroup.all);
  const columns = useMemo(
    () => drillDetailColumns(t, page, preferredUnits),
    [t, page, preferredUnits],
  );

  const formatCsvValue = useCallback(
    (field: string, raw: DetailRow[keyof DetailRow]) => {
      switch (field) {
        case 'sessionDate':
          return raw
            ? DateTime.fromJSDate(raw as DetailRow['sessionDate']).toFormat(
                'M/d/yyyy',
              )
            : '-';
        case 'sessionTime':
        case 'duration':
        case 'drillRunDurationAvg':
          return formatTime(raw as number | null | undefined);
        case 'name':
          return raw ?? '-';
        case 'playerLoadAvg':
          return formatNum({
            value: raw as number | null | undefined,
            multiplier: 0.01,
          });
        case 'playerLoadPerMinAvg':
          return formatNum({
            value: raw as number | null | undefined,
            multiplier: 0.01,
            decimals: 2,
          });
        case 'drillRunCountAvg':
          return formatNum({
            value: raw as number | null | undefined,
            decimals: 1,
          });
        case 'speedAvgAvg':
        case 'speedMaxAvg':
          return formatNum({
            value: raw as number | null | undefined,
            multiplier: getSpeedMultiplier(preferredUnits === 'metric'),
          });
        case 'accelAvgAvg':
        case 'accelMaxAvg':
          return formatNum({
            value: raw as number | null | undefined,
            multiplier: 1 / 9.8,
            decimals: 2,
          });
        case 'strideRateAvgAvg':
        case 'hrAvgAvg':
        case 'hrMaxAvg':
          return formatNum({
            value: raw as number | null | undefined,
            decimals: 0,
          });
        case 'balanceNormalizedAvg':
          return formatNum({
            value: raw as number | null | undefined,
            multiplier: 100,
            decimals: 0,
          });
        default:
          return raw;
      }
    },
    [preferredUnits],
  );

  const csvFormatConfig = useMemo(() => {
    const speedMultiplier = getSpeedMultiplier(preferredUnits === 'metric');
    const speedUnit = preferredUnits === 'metric' ? 'kph' : 'mph';
    return new Map<
      string,
      {unit: string; multiplier?: number; decimals?: number}
    >([
      ['speedAvgAvg', {unit: speedUnit, multiplier: speedMultiplier}],
      ['speedMaxAvg', {unit: speedUnit, multiplier: speedMultiplier}],
      ['accelAvgAvg', {unit: 'g', multiplier: 1 / 9.8, decimals: 2}],
      ['accelMaxAvg', {unit: 'g', multiplier: 1 / 9.8, decimals: 2}],
      ['strideRateAvgAvg', {unit: 'spm', decimals: 0}],
      ['balanceNormalizedAvg', {unit: '%', multiplier: 100, decimals: 0}],
      ['hrAvgAvg', {unit: 'bpm', decimals: 0}],
      ['hrMaxAvg', {unit: 'bpm', decimals: 0}],
    ]);
  }, [preferredUnits]);

  const detailRows = useMemo<DetailRow[]>(() => {
    return data
      .filter(d => d.drillDetail.positionGroup === selectedGroup)
      .map(d => {
        return {
          sessionDate: new Date(d.teamSessionSessionEvent?.startTime as string),
          sessionTime:
            (new Date(
              d.teamSessionSessionEvent?.startTime as string,
            ).getTime() -
              new Date(d.teamSession?.startTime as string).getTime()) /
            1000,
          duration: d.teamSessionSessionEvent?.endTime
            ? (new Date(
                d.teamSessionSessionEvent?.endTime as string,
              ).getTime() -
                new Date(
                  d.teamSessionSessionEvent?.startTime as string,
                ).getTime()) /
              1000
            : 0,
          sessionType: d.teamSession?.type ?? '-',
          name: d.drill?.name ?? '-',
          ...d.drillDetail,
        };
      });
  }, [data, selectedGroup]);

  const handleRowClick = useCallback(
    (params: GridRowParams) => {
      const videoUrl = params.row.videoUrl;
      if (videoUrl && onVideoSelect) {
        onVideoSelect(videoUrl);
      }
    },
    [onVideoSelect],
  );

  const handleExport = useCallback(() => {
    const visibleColumns = columns.filter(c => c.field !== 'id' && !c.hide);
    const header = visibleColumns
      .map(c => {
        const name = c.headerName ?? c.field;
        const config = csvFormatConfig.get(c.field);
        return config ? `${name} ${config.unit}` : name;
      })
      .join(',');
    const sortedRows = [...detailRows].sort(
      (a, b) =>
        new Date(b.sessionDate).getTime() - new Date(a.sessionDate).getTime(),
    );
    const csvRows = sortedRows.map(row =>
      visibleColumns
        .map(col => {
          const raw = row[col.field as keyof typeof row];
          const formatted = formatCsvValue(col.field, raw);
          const str = String(formatted ?? '');
          return str.includes(',') ? `"${str}"` : str;
        })
        .join(','),
    );
    const csv = [header, ...csvRows].join('\n');
    const blob = new Blob([csv], {type: 'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `HELIOS-${sanitizeFilename(teamName ?? '')}-${sanitizeFilename(drillName ?? '')}-${selectedGroup}-${DateTime.local().toFormat('yyyy-MM-dd-HH-mm')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [
    columns,
    csvFormatConfig,
    detailRows,
    drillName,
    formatCsvValue,
    selectedGroup,
    teamName,
  ]);

  if (!data.length) {
    return null;
  }

  return (
    <Box sx={sx}>
      <Box>
        <Tabs
          value={selectedGroup}
          onChange={(_e, val) => setSelectedGroup(val)}>
          {Object.values(PositionGroup).map(group => (
            <Tab key={group} label={group} value={group} />
          ))}
        </Tabs>
      </Box>
      <Box sx={{mt: 1}}>
        <Paper sx={{p: 2}}>
          {title ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: {xs: 'column', sm: 'row'},
                alignItems: {sm: 'center'},
                gap: 1,
                mb: 2,
              }}>
              <Typography variant="h3">{title}</Typography>
              <Box sx={{flexGrow: 1}} />
              <Alert severity="info" sx={{py: 0}}>
                {t('library.scrollToViewMore')}
              </Alert>
              {page === 'drillDetail' && (
                <Button size="small" onClick={handleExport}>
                  {t('general.export')}
                </Button>
              )}
            </Box>
          ) : null}
          <DataGrid
            rows={detailRows}
            columns={columns}
            density="compact"
            disableSelectionOnClick
            autoHeight
            onRowClick={handleRowClick}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: onVideoSelect ? 'pointer' : 'default',
              },
            }}
            initialState={{
              sorting: {
                sortModel:
                  page === 'drillDetail'
                    ? [{field: 'sessionDate', sort: 'desc'}]
                    : [{field: 'sessionTime', sort: 'asc'}],
              },
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

export default DrillDetailsDataGrid;
